// Per-rec-league "show drafts" admin preference. Stored as a host-only cookie
// so the server can read it during SSR (see `shouldIncludeDrafts`). The cookie
// is only written when an admin explicitly enables drafts for a league, and is
// removed when disabled — the absence of the cookie means "drafts hidden".
//
// This module is intentionally dependency-free (no js-cookie) so it can be
// safely imported from both server and client code.
export const getShowDraftsCookieName = (recLeagueId: number) =>
  `recLeague_${recLeagueId}_showDrafts`;

export const SHOW_DRAFTS_COOKIE_TTL_DAYS = 7;

// Matches the cookie name produced by `getShowDraftsCookieName`.
const SHOW_DRAFTS_COOKIE_REGEX = /^recLeague_\d+_showDrafts$/;

/**
 * Client-side: persist the show-drafts preference for a league (writes the
 * cookie only if not already "true"). Used by the `?show_drafts=true` preview
 * link in the content-import notification email so an admin's draft view sticks
 * across navigation, not just on the linked page. No-op on the server, and only
 * meaningful for admins (the server gate in `shouldIncludeDrafts` still checks
 * admin), so a non-admin writing it changes nothing.
 */
export const setShowDraftsCookie = (recLeagueId: number) => {
  if (typeof document === "undefined") return;
  const name = getShowDraftsCookieName(recLeagueId);
  const alreadyOn = document.cookie
    .split(";")
    .some((c) => c.trim() === `${name}=true`);
  if (alreadyOn) return;
  const maxAge = SHOW_DRAFTS_COOKIE_TTL_DAYS * 24 * 60 * 60;
  document.cookie = `${name}=true; Max-Age=${maxAge}; path=/; SameSite=Lax`;
};

/**
 * Removes stale show-drafts cookies from the current host.
 *
 * Historically this cookie was written on every mount of any rec-league card
 * (even with the default value of "false"), so browsing the cross-league feed /
 * explore / search pages accumulated hundreds of `recLeague_<id>_showDrafts`
 * cookies on a single host. Once the cumulative cookie header grew large enough,
 * the server rejected requests with HTTP 431 (Request Header Fields Too Large).
 *
 * We now only persist this cookie when an admin actively enables drafts, so any
 * cookie whose value is not "true" is stale and safe to delete. This runs on
 * client boot to self-heal already-affected browsers.
 */
export const cleanupStaleShowDraftsCookies = () => {
  if (typeof document === "undefined") return;

  try {
    const pairs = document.cookie ? document.cookie.split(";") : [];
    for (const pair of pairs) {
      const eq = pair.indexOf("=");
      const name = (eq > -1 ? pair.slice(0, eq) : pair).trim();
      const value = eq > -1 ? pair.slice(eq + 1).trim() : "";
      if (SHOW_DRAFTS_COOKIE_REGEX.test(name) && value !== "true") {
        // Expire the host-only, path=/ cookie that was originally written.
        document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`;
      }
    }
  } catch {
    // Cookie access can throw in some sandboxed contexts; cleanup is best-effort.
  }
};
